import type { JsonApiAtomicDocument, JsonApiDocument, JsonApiResource } from './json-api.ts'

const CONTENT_TYPE_JSON_API = 'application/vnd.api+json'
const CONTENT_TYPE_JSON_API_ATOMIC = 'application/vnd.api+json; ext="https://jsonapi.org/ext/atomic"'
const HTTP_ERROR_PREFIX = 'HTTP error! status: '
const HEADER_ACCEPT = 'Accept'
const HEADER_CONTENT_TYPE = 'Content-Type'
const METHOD_POST = 'POST'

function resolvePath(...segments: string[]): string {
  return new URL(segments.join('/')).href
}

export interface PageOption {
  size?: number
  number?: number
}

export interface FetchOptions {
  fields?: Record<string, string[]>
  page?: PageOption
  include?: string[]
  filter?: string
  headers?: HeadersInit
  body?: BodyInit
  signal?: AbortSignal
}

export interface FetchParams {
  [key: string]: string
}

export interface Options {
  searchParams?: URLSearchParams
  headers: Headers
  method?: string
  body?: BodyInit
  signal?: AbortSignal
}

class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message)
    this.name = 'HttpError'
  }
}

async function tryJson(response: Response) {
  try {
    return await response.json()
  } catch {
    // Ignore JSON parsing errors
  }
}

async function req(url: string, options: Options) {
  const { headers, searchParams, method, signal, body } = options
  const textSearchParams = searchParams ? `?${searchParams}` : ''
  const finalUrl = url.replace(/(?:\?.*?)?(?=#|$)/, textSearchParams)
  const response = await fetch(finalUrl, {
    method,
    headers,
    signal,
    body,
  })
  const responseBody = await tryJson(response)
  if (!response.ok)
    throw new HttpError(`${HTTP_ERROR_PREFIX}${response.status} ${response.statusText}`, response.status, responseBody)
  const data = responseBody as JsonApiDocument
  return data
}

async function postAtomic(url: string, options: FetchOptions) {
  const { signal, body } = options
  const method = METHOD_POST
  const headers = new Headers(options.headers ?? {})
  headers.append(HEADER_ACCEPT, CONTENT_TYPE_JSON_API_ATOMIC)
  headers.append(HEADER_CONTENT_TYPE, CONTENT_TYPE_JSON_API_ATOMIC)
  const response = await fetch(url, {
    method,
    headers,
    signal,
    body,
  })
  const responseBody = await tryJson(response)
  if (!response.ok)
    throw new HttpError(`${HTTP_ERROR_PREFIX}${response.status} ${response.statusText}`, response.status, responseBody)
  if (response.status === 204) return
  const data = responseBody as JsonApiAtomicDocument
  return data
}

export type JsonApiFetcher = InstanceType<typeof JsonApiFetcherImpl>

export class JsonApiFetcherImpl implements JsonApiFetcher {
  constructor(public endpoint: string) {}
  createOptions(options: FetchOptions = {}, params: FetchParams = {}, body?: BodyInit): Options {
    const searchParams = new URLSearchParams()
    const headers = new Headers(options.headers ?? {})
    headers.append(HEADER_ACCEPT, CONTENT_TYPE_JSON_API)
    const requestOptions = { searchParams, headers, body }
    if (options.fields)
      for (const [key, value] of Object.entries(options.fields)) searchParams.append(`fields[${key}]`, value.join(','))
    if (options.page?.size) searchParams.append('page[size]', options.page.size.toString())
    if (options.page?.number) searchParams.append('page[number]', options.page.number.toString())
    if (options.include) searchParams.append('include', options.include.join(','))
    if (options.filter) searchParams.append('filter', options.filter)
    for (const [key, value] of Object.entries(params)) searchParams.append(key, value)
    return requestOptions
  }
  async fetchDocument(type: string, id?: string, options?: FetchOptions, params?: FetchParams) {
    const segments = [this.endpoint, type]
    if (id) segments.push(id)
    const url = resolvePath(...segments)
    const doc = await req(url, this.createOptions(options, params))
    return doc
  }
  async fetchAll(type: string, options?: FetchOptions, params?: FetchParams) {
    const url = resolvePath(this.endpoint, type)
    const doc = await req(url, this.createOptions(options, params))
    const resources = doc.data as JsonApiResource[]
    return resources
  }
  async fetchOne(type: string, id: string, options?: FetchOptions, params?: FetchParams) {
    const url = resolvePath(this.endpoint, type, id)
    const doc = await req(url, this.createOptions(options, params))
    const resource = doc.data as JsonApiResource
    return resource
  }
  async fetchHasMany(type: string, id: string, name: string, options?: FetchOptions, params?: FetchParams) {
    const url = resolvePath(this.endpoint, type, id, name)
    const doc = await req(url, this.createOptions(options, params))
    return doc
  }
  async fetchBelongsTo(type: string, id: string, name: string, options?: FetchOptions, params?: FetchParams) {
    const url = resolvePath(this.endpoint, type, id, name)
    const doc = await req(url, this.createOptions(options, params))
    return doc
  }
  async post(resource: JsonApiResource, options?: FetchOptions) {
    const url = resolvePath(this.endpoint, resource.type)
    const postDoc: JsonApiDocument = {
      data: resource,
    }
    const body = JSON.stringify(postDoc)
    const newOptions = this.createOptions(options, {}, body)
    newOptions.method = METHOD_POST
    newOptions.headers.set(HEADER_CONTENT_TYPE, CONTENT_TYPE_JSON_API)
    const doc = await req(url, newOptions)
    return doc
  }
  async patch(resource: JsonApiResource, options?: FetchOptions) {
    if (!resource.id) throw new Error('Resource must have an id to be patched')
    const url = resolvePath(this.endpoint, resource.type, resource.id)
    const patchDoc: JsonApiDocument = {
      data: resource,
    }
    const body = JSON.stringify(patchDoc)
    const headers = new Headers(options?.headers ?? {})
    headers.set(HEADER_CONTENT_TYPE, CONTENT_TYPE_JSON_API)
    headers.append(HEADER_ACCEPT, CONTENT_TYPE_JSON_API)
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      signal: options?.signal,
      body,
    })
    const responseBody = await tryJson(response)
    if (!response.ok)
      throw new HttpError(
        `${HTTP_ERROR_PREFIX}${response.status} ${response.statusText}`,
        response.status,
        responseBody,
      )
    if (response.status === 204) return
    const doc = responseBody as JsonApiDocument
    return doc
  }
  async postAtomic(doc: JsonApiAtomicDocument, options: FetchOptions = {}) {
    const url = new URL([this.endpoint, 'operations'].join('/')).href
    options.body = JSON.stringify(doc)
    const results = await postAtomic(url, options)
    return results
  }
}
